import { isEmpty, isNil, isNull, trim } from 'lodash';
import type { FeatureResponseData } from 'shared/api';
import { MarkArea } from '../../../shared';
import type { DrillAndBlastFormType } from './types';

/**
 * Extracts unique IDs from drawn items (polygons or lines).
 * Handles both string IDs and object IDs.
 *
 * @param drawnItems Array of drawn items (can be strings or objects with id property)
 * @returns Object containing unique IDs array, count, and the last ID (or null)
 */
export const extractUniqueIdsFromDrawnItems = (
  drawnItems: unknown[],
): {
  uniqueIds: string[];
  count: number;
  lastId: string | null;
} => {
  const ids = drawnItems
    .map((item) =>
      typeof item === 'string' ? item : ((item as { id?: string })?.id ?? ''),
    )
    .filter((id) => id !== '');

  const uniqueIds = Array.from(new Set(ids));

  return {
    uniqueIds,
    count: uniqueIds.length,
    lastId: uniqueIds.length > 0 ? uniqueIds[uniqueIds.length - 1] : null,
  };
};

/**
 * Normalizes expected number of holes input to a number or undefined.
 * Handles string, number, null, and undefined inputs.
 *
 * @param rawHoles Raw input value (can be string, number, null, or undefined)
 * @returns Normalized number or undefined
 */
export const normalizeExpectedNumberOfHoles = (
  rawHoles: unknown,
): number | undefined => {
  if (isNil(rawHoles)) {
    return undefined;
  }

  if (typeof rawHoles === 'string') {
    return rawHoles.trim() === '' ? undefined : Number(rawHoles);
  }

  if (typeof rawHoles === 'number') {
    return rawHoles;
  }

  return undefined;
};

/**
 * Extracts WKT string from exported items (polygons or lines) based on target ID.
 * Falls back to first item's WKT if target ID not found.
 *
 * @param exportedItems Array of exported items with id and wkt properties
 * @param targetId Target ID to find, or null to use first item
 * @param fallbackValue Fallback value if no WKT found (default: '')
 * @returns WKT string or fallback value
 */
export const extractWktFromExportedItems = (
  exportedItems: Array<{ id: string | number; wkt: string }>,
  targetId: string | null,
  fallbackValue: string | undefined = '',
): string | undefined => {
  const foundItem = exportedItems.find((item) => item.id === targetId);
  return foundItem?.wkt ?? exportedItems[0]?.wkt ?? fallbackValue;
};

/**
 * Extracts WKT geometry from feature response data based on target ID.
 * Falls back to first feature's geometry if target ID not found.
 *
 * @param features Array of feature response data
 * @param targetId Target ID to find, or null to use first feature
 * @param fallbackValue Fallback value if no geometry found (default: '')
 * @returns Geometry string or fallback value
 */
export const extractWktFromFeatures = (
  features: FeatureResponseData[],
  targetId: string | null,
  fallbackValue: string | undefined = '',
): string | undefined => {
  const feature = features.find((feat) => feat.id === targetId) ?? features[0];
  return feature?.geometry ?? fallbackValue;
};

/**
 * Validates if all required fields for drill and blast form are filled.
 * Checks numeric fields, dropdowns, title, and AOI (either drawn or from workspace layer).
 *
 * @param values Form values object
 * @param drawnPolygons Array of drawn polygons from polygon tool (can be strings or objects with id)
 * @param aoiMarkedArea Current marked area mode (Draw or LayerFromWorkspace)
 * @returns true if all required fields are valid, false otherwise
 */
export const areRequiredFieldsValid = (
  values: DrillAndBlastFormType,
  drawnPolygons: unknown[],
  aoiMarkedArea: MarkArea,
): boolean => {
  const hasNumbers =
    !isNull(values.spacingMeters) &&
    !isNull(values.burdenMeters) &&
    !isNull(values.baseElevationMeters);
  const hasDropdowns = !!values.drillPattern && !!values.explosiveType;
  const hasTitle = !isEmpty(trim(values.title));

  // For Draw mode, require exactly one polygon present.
  const drawModeHasAoi =
    aoiMarkedArea === MarkArea.Draw && Array.isArray(drawnPolygons)
      ? extractUniqueIdsFromDrawnItems(drawnPolygons).count === 1
      : false;

  const layerModeHasAoi =
    aoiMarkedArea === MarkArea.LayerFromWorkspace &&
    !isNil(values.aoiWorkspaceLayer?.value);
  const hasAoi = drawModeHasAoi || layerModeHasAoi;

  return hasNumbers && hasDropdowns && hasTitle && hasAoi;
};
